/**
 * Celeste Dice Component
 *
 * Renders the celeste-themed dice with cloud decorations on the body
 * and 4-pointed diamond stars instead of circular dots.
 * Background is shared across all colors; only the diamond shapes change per color.
 *
 * SVG structure extracted from Figma exports (blue_1 reference for background).
 * Reuses useDiceViewModel for shared animation/interaction logic.
 */

import React from 'react';
import { motion } from 'framer-motion';
import type { DiceProps } from '../basics/constants/types';
import type { CelesteDiceColorScheme } from './constants/diceColorSchemes';
import { useDiceViewModel } from '../basics/hooks/useDiceViewModel';
import { GRASS_TEXTURE_BASE64, DICE_CONFIG } from '../basics/utils/utils';
import {
  CELESTE_PATHS,
  CELESTE_GRADIENTS,
  TEXTURE_RECT,
  DIAMOND_LAYOUTS,
  outerDiamondPath,
  innerDiamondPath
} from './constants/celesteDiceData';
import '../basics/Dice.css';

interface CelesteDiceProps extends DiceProps {
  colorScheme: CelesteDiceColorScheme;
}

const GRADIENT_OVERLAY_OPACITY = 0.4;

const CelesteDiceComponent = React.memo(function CelesteDiceComponent({
  colorScheme,
  ...diceProps
}: CelesteDiceProps) {
  const {
    showDiceFace = true,
    showGround = true,
    isRolling = false,
    showGrassTexture = true,
    viewBox = '0 0 187 198'
  } = diceProps;

  const { state, actions } = useDiceViewModel(diceProps);

  // Determine currently displayed value (cycles during rolling animation)
  const diamonds = DIAMOND_LAYOUTS[state.displayedValue] ?? [];

  const getId = state.getId;
  const g = CELESTE_GRADIENTS;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={viewBox}
      className={state.svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      role="img"
      aria-label={`Dice showing ${String(state.validValue)}`}
      aria-live={isRolling ? 'polite' : 'off'}
      focusable="false"
      onClick={actions.handleClick}
      style={{ cursor: state.cursorStyle }}
    >
      <defs>
        {/* Clip path */}
        <clipPath id={getId('clip')}>
          <rect width="186.67" height="197.33" fill="white" />
        </clipPath>

        {/* Drop shadow filter (for animated face group) */}
        <filter id={getId('dropShadow')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation={DICE_CONFIG.SHADOW_3D.BLUR_RADIUS} />
          <feOffset
            dx={DICE_CONFIG.SHADOW_3D.OFFSET_X}
            dy={DICE_CONFIG.SHADOW_3D.OFFSET_Y}
            result="offsetblur"
          />
          <feComponentTransfer>
            <feFuncA type="linear" slope={DICE_CONFIG.SHADOW_3D.OPACITY} />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Inner bevel filter for white face */}
        <filter id={getId('innerBevel')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur
            in="SourceAlpha"
            stdDeviation={DICE_CONFIG.BEVEL.BLUR_RADIUS}
            result="blur"
          />
          <feOffset
            in="blur"
            dx={DICE_CONFIG.BEVEL.HIGHLIGHT_OFFSET}
            dy={DICE_CONFIG.BEVEL.HIGHLIGHT_OFFSET}
            result="offsetBlur"
          />
          <feFlood
            floodColor="white"
            floodOpacity={DICE_CONFIG.BEVEL.HIGHLIGHT_OPACITY}
            result="highlight"
          />
          <feComposite in="highlight" in2="offsetBlur" operator="in" result="topHighlight" />
          <feOffset
            in="blur"
            dx={DICE_CONFIG.BEVEL.SHADOW_OFFSET}
            dy={DICE_CONFIG.BEVEL.SHADOW_OFFSET}
            result="offsetBlur2"
          />
          <feFlood
            floodColor="black"
            floodOpacity={DICE_CONFIG.BEVEL.SHADOW_OPACITY}
            result="shadow"
          />
          <feComposite in="shadow" in2="offsetBlur2" operator="in" result="bottomShadow" />
          <feMerge>
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="topHighlight" />
            <feMergeNode in="bottomShadow" />
          </feMerge>
        </filter>

        {/* Dark shadow gradient */}
        <linearGradient
          id={getId('shadow')}
          x1={g.SHADOW.x1}
          y1={g.SHADOW.y1}
          x2={g.SHADOW.x2}
          y2={g.SHADOW.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.SHADOW.start} />
          <stop offset="1" stopColor={g.SHADOW.end} />
        </linearGradient>

        {/* Silver ledge gradient */}
        <linearGradient
          id={getId('ledge')}
          x1={g.LEDGE.x1}
          y1={g.LEDGE.y1}
          x2={g.LEDGE.x2}
          y2={g.LEDGE.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.LEDGE.start} />
          <stop offset="1" stopColor={g.LEDGE.end} />
        </linearGradient>

        {/* Celeste body gradient */}
        <linearGradient
          id={getId('body')}
          x1={g.BODY.x1}
          y1={g.BODY.y1}
          x2={g.BODY.x2}
          y2={g.BODY.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.BODY.stops[0]} />
          <stop offset="0.48" stopColor={g.BODY.stops[1]} />
          <stop offset="1" stopColor={g.BODY.stops[2]} />
        </linearGradient>

        {/* Cloud gradients (same celeste colors, different orientations) */}
        <linearGradient
          id={getId('cloud1')}
          x1={g.CLOUD_BOTTOM.x1}
          y1={g.CLOUD_BOTTOM.y1}
          x2={g.CLOUD_BOTTOM.x2}
          y2={g.CLOUD_BOTTOM.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.BODY.stops[0]} />
          <stop offset="0.48" stopColor={g.BODY.stops[1]} />
          <stop offset="1" stopColor={g.BODY.stops[2]} />
        </linearGradient>
        <linearGradient
          id={getId('cloud2')}
          x1={g.CLOUD_RIGHT.x1}
          y1={g.CLOUD_RIGHT.y1}
          x2={g.CLOUD_RIGHT.x2}
          y2={g.CLOUD_RIGHT.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.BODY.stops[0]} />
          <stop offset="0.48" stopColor={g.BODY.stops[1]} />
          <stop offset="1" stopColor={g.BODY.stops[2]} />
        </linearGradient>
        <linearGradient
          id={getId('cloud3')}
          x1={g.CLOUD_TOP_RIGHT.x1}
          y1={g.CLOUD_TOP_RIGHT.y1}
          x2={g.CLOUD_TOP_RIGHT.x2}
          y2={g.CLOUD_TOP_RIGHT.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.BODY.stops[0]} />
          <stop offset="0.48" stopColor={g.BODY.stops[1]} />
          <stop offset="1" stopColor={g.BODY.stops[2]} />
        </linearGradient>
        <linearGradient
          id={getId('cloud4')}
          x1={g.CLOUD_TOP_LEFT.x1}
          y1={g.CLOUD_TOP_LEFT.y1}
          x2={g.CLOUD_TOP_LEFT.x2}
          y2={g.CLOUD_TOP_LEFT.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.BODY.stops[0]} />
          <stop offset="0.48" stopColor={g.BODY.stops[1]} />
          <stop offset="1" stopColor={g.BODY.stops[2]} />
        </linearGradient>

        {/* Grass/cloud texture pattern */}
        {showGrassTexture && (
          <pattern
            id={getId('texture')}
            patternContentUnits="objectBoundingBox"
            width="1"
            height="1"
          >
            <use xlinkHref={`#${getId('textureImg')}`} transform="scale(0.00436681)" />
          </pattern>
        )}
        {showGrassTexture && (
          <image
            id={getId('textureImg')}
            width="229"
            height="229"
            preserveAspectRatio="none"
            xlinkHref={GRASS_TEXTURE_BASE64}
          />
        )}

        {/* White face gradient overlays */}
        <linearGradient
          id={getId('whiteVert')}
          x1={g.WHITE_VERT.x1}
          y1={g.WHITE_VERT.y1}
          x2={g.WHITE_VERT.x2}
          y2={g.WHITE_VERT.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.WHITE_BORDER.color} />
          <stop offset="0.08" stopColor={g.WHITE_BORDER.transparent} stopOpacity="0" />
          <stop offset="0.92" stopColor={g.WHITE_BORDER.transparent} stopOpacity="0" />
          <stop offset="1" stopColor={g.WHITE_BORDER.color} />
        </linearGradient>
        <linearGradient
          id={getId('whiteHoriz')}
          x1={g.WHITE_HORIZ.x1}
          y1={g.WHITE_HORIZ.y1}
          x2={g.WHITE_HORIZ.x2}
          y2={g.WHITE_HORIZ.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.WHITE_BORDER.color} />
          <stop offset="0.08" stopColor={g.WHITE_BORDER.transparent} stopOpacity="0" />
          <stop offset="0.92" stopColor={g.WHITE_BORDER.transparent} stopOpacity="0" />
          <stop offset="1" stopColor={g.WHITE_BORDER.color} />
        </linearGradient>

        {/* Diamond inner gradient (objectBoundingBox — same proportions for all diamond sizes) */}
        <linearGradient
          id={getId('diamondGrad')}
          gradientUnits="objectBoundingBox"
          x1="0.29"
          y1="0.71"
          x2="0.71"
          y2="0.29"
        >
          <stop offset="0" stopColor={colorScheme.innerGradient[0]} />
          <stop offset="0.48" stopColor={colorScheme.innerGradient[1]} />
          <stop offset="1" stopColor={colorScheme.innerGradient[2]} />
        </linearGradient>
      </defs>

      <g clipPath={`url(#${getId('clip')})`}>
        {/* Fixed ground/background — only rendered when showGround */}
        {showGround && (
          <>
            {/* Dark shadow base */}
            <path d={CELESTE_PATHS.DARK_SHADOW} fill={`url(#${getId('shadow')})`} />

            {/* Silver ledge */}
            <path d={CELESTE_PATHS.SILVER_LEDGE} fill={`url(#${getId('ledge')})`} />

            {/* Celeste body */}
            <path d={CELESTE_PATHS.BODY_RECT} fill={`url(#${getId('body')})`} />

            {/* Cloud decorations */}
            <path d={CELESTE_PATHS.CLOUD_BOTTOM} fill={`url(#${getId('cloud1')})`} />
            <path d={CELESTE_PATHS.CLOUD_RIGHT} fill={`url(#${getId('cloud2')})`} />
            <path d={CELESTE_PATHS.CLOUD_TOP_RIGHT} fill={`url(#${getId('cloud3')})`} />
            <path d={CELESTE_PATHS.CLOUD_TOP_LEFT} fill={`url(#${getId('cloud4')})`} />

            {/* Texture overlay */}
            {showGrassTexture && (
              <rect
                x={TEXTURE_RECT.x}
                y={TEXTURE_RECT.y}
                width={TEXTURE_RECT.width}
                height={TEXTURE_RECT.height}
                fill={`url(#${getId('texture')})`}
              />
            )}
          </>
        )}

        {/* Animated white face + diamond stars */}
        {showDiceFace && (
          <motion.g
            animate={state.combinedAnimation}
            className="dice-3d-transform"
            style={{
              transformOrigin: '90.23px 90.91px',
              opacity: isRolling ? DICE_CONFIG.INACTIVE_OPACITY : 1
            }}
            filter={`url(#${getId('dropShadow')})`}
          >
            {/* White face base with bevel effect */}
            <path
              d={CELESTE_PATHS.WHITE_FACE}
              fill="white"
              filter={`url(#${getId('innerBevel')})`}
            />

            {/* White face vertical gradient overlay */}
            <path
              d={CELESTE_PATHS.WHITE_FACE}
              fill={`url(#${getId('whiteVert')})`}
              opacity={GRADIENT_OVERLAY_OPACITY}
            />

            {/* White face horizontal gradient overlay */}
            <path
              d={CELESTE_PATHS.WHITE_FACE_HORIZ}
              fill={`url(#${getId('whiteHoriz')})`}
              opacity={GRADIENT_OVERLAY_OPACITY}
            />

            {/* Diamond stars */}
            {diamonds.map((diamond, index) => (
              <React.Fragment key={index}>
                <path d={outerDiamondPath(diamond)} fill={colorScheme.outerFill} />
                <path d={innerDiamondPath(diamond)} fill={`url(#${getId('diamondGrad')})`} />
              </React.Fragment>
            ))}
          </motion.g>
        )}
      </g>
    </svg>
  );
});

export default CelesteDiceComponent;
